import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSearch,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { EEA_COUNTRY_LIST } from '@shared-web/constants/country';
import { RegExAddress, RegExBankInfo } from '@shared-web/constants/regex';
import US_STATES from '@shared-web/constants/us-state.json';
import { useCountry } from '@shared-web/modules/user/hooks/useCountry';

// Dynamic form schema based on user country
const createFormSchema = (isUS: boolean) => {
  const baseSchema = {
    streetName: z
      .string()
      .min(1, { message: 'Street name is required' })
      .regex(RegExAddress, { message: 'Street name is invalid' }),
    buildingName: z
      .string()
      .min(1, { message: 'Building name is required' })
      .regex(RegExAddress, { message: 'Building name is invalid' }),
    city: z
      .string()
      .min(1, { message: 'City is required' })
      .regex(RegExBankInfo, { message: 'City is invalid' }),
    state: z
      .string()
      .min(1, { message: 'State is required' })
      .regex(RegExBankInfo, { message: 'State is invalid' }),
    country: z.string().min(1, { message: 'Country is required' }),
    agreeToTerms: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the terms and conditions',
    }),
  };

  if (isUS) {
    return z.object({
      ...baseSchema,
      ssn: z
        .string()
        .min(1, { message: 'SSN is required' })
        .regex(/^\d{3}-?\d{2}-?\d{4}$/, {
          message: 'Invalid SSN format (e.g., 123-45-6789)',
        }),
      zipCode: z
        .string()
        .min(1, { message: 'Zip code is required' })
        .regex(/^\d{5}(-\d{4})?$/, { message: 'Invalid zip code format' }),
    });
  }

  // Non-US users (UK, EEA, etc.)
  return z.object({
    ...baseSchema,
    ssn: z.string().optional(),
    zipCode: z.string().min(1, { message: 'Zip code is required' }),
  });
};

type FormSchema = z.infer<ReturnType<typeof createFormSchema>>;

export interface AddressFormProps {
  onSubmit: (data: FormSchema) => void | Promise<void>;
  onClose?: () => void;
  defaultValues?: Partial<FormSchema>;
  isSubmitting?: boolean;
}

export function AddressForm({
  onSubmit,
  onClose,
  defaultValues,
  isSubmitting = false,
}: AddressFormProps) {
  const { countryState, countryCode } = useCountry();
  const { t } = useTranslation(['country']);

  const isUS = useMemo(() => countryState.isUS, [countryState]);
  const isEEA = useMemo(() => countryState.isEEA, [countryState]);

  const formSchema = useMemo(() => createFormSchema(isUS), [isUS]);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ssn: defaultValues?.ssn ?? '',
      streetName: defaultValues?.streetName ?? '',
      buildingName: defaultValues?.buildingName ?? '',
      city: defaultValues?.city ?? '',
      state: defaultValues?.state ?? '',
      country: defaultValues?.country ?? 'United States',
      zipCode: defaultValues?.zipCode ?? '',
      agreeToTerms: true,
    },
  });
  const [stateSearchText, setStateSearchText] = useState('');

  useEffect(() => {
    form.setValue('country', countryCode);
  }, [isEEA, countryCode, form]);

  return (
    <div className="w-full p-4 md:w-180 md:p-0">
      {/* Header */}
      <h2 className="text-foreground text-h3">
        Verify your identity to activate bank transfers
      </h2>

      {/* Description */}
      <p className="text-muted-foreground text-body1 mt-3 mb-8">
        To comply with financial regulations, we need to confirm your identity
        before enabling deposit transfers. This helps keep your account secure
        and ensures smooth processing of future transactions.
      </p>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          {/* SSN Field */}
          {isUS && (
            <FormField
              control={form.control}
              name="ssn"
              render={({
                field,
              }: {
                field: ControllerRenderProps<FormSchema, 'ssn'>;
              }) => (
                <FormItem className="w-80">
                  <FormLabel>Social Security Number (SSN)</FormLabel>
                  <FormControl>
                    <Input
                      error={!!form.formState.errors.ssn}
                      {...field}
                      placeholder="Enter your SSN"
                    />
                  </FormControl>
                  <FormDescription className="text-muted-foreground text-body2">
                    Used only for verification - your data will be handled
                    securely and will not be used for credit checks.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Address Section */}
          <div className="flex flex-col gap-3">
            <p className="text-muted-foreground text-label1">
              Your residential address details. P.O. boxes are not accepted.
            </p>

            {/* Row 1: Street Name & Building Name */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 md:gap-y-2">
              {/* Street Name */}
              <FormField
                control={form.control}
                name="streetName"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'streetName'>;
                }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Street name</FormLabel>
                    <FormControl>
                      <Input
                        error={!!form.formState.errors.streetName}
                        {...field}
                        placeholder="Enter street name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Building Name */}
              <FormField
                control={form.control}
                name="buildingName"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'buildingName'>;
                }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Apartment, building, unit No., etc.</FormLabel>
                    <FormControl>
                      <Input
                        error={!!form.formState.errors.buildingName}
                        {...field}
                        placeholder="Enter building name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 2: State & City */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 md:gap-y-2">
              {/* State */}
              <FormField
                control={form.control}
                name="state"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'state'>;
                }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>State / province / region</FormLabel>
                    {isUS ? (
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        searchable
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Enter state/ province / region" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent
                          header={
                            <SelectSearch
                              className="h-8"
                              onSearch={setStateSearchText}
                              placeholder="Search state"
                            />
                          }
                        >
                          {Object.entries(US_STATES)
                            .filter(([, value]) =>
                              stateSearchText
                                ? value
                                    .toLowerCase()
                                    .includes(stateSearchText.toLowerCase())
                                : true,
                            )
                            .map(([key, value]) => (
                              <SelectItem key={key} value={key}>
                                {value as string}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <FormControl>
                        <Input
                          error={!!form.formState.errors.state}
                          {...field}
                          placeholder="Enter state/ province / region"
                        />
                      </FormControl>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* City */}
              <FormField
                control={form.control}
                name="city"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'city'>;
                }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input
                        error={!!form.formState.errors.city}
                        {...field}
                        placeholder="Enter city"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 3: Country & Zip Code */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 md:gap-y-2">
              {/* Country */}
              <FormField
                control={form.control}
                name="country"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'country'>;
                }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Country</FormLabel>
                    {isEEA ? (
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={countryCode}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {EEA_COUNTRY_LIST.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <FormControl>
                        <Input
                          error={!!form.formState.errors.country}
                          placeholder="Enter country"
                          disabled
                          value={t(
                            `country_full_name__${countryCode.toLowerCase()}`,
                          )}
                        />
                      </FormControl>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Zip Code */}
              <FormField
                control={form.control}
                name="zipCode"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'zipCode'>;
                }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Zip code</FormLabel>
                    <FormControl>
                      <Input
                        error={!!form.formState.errors.zipCode}
                        {...field}
                        placeholder="Enter zip code"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isSubmitting || !form.watch('agreeToTerms')}
            >
              {isSubmitting ? 'Submitting...' : 'Continue'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
